import { css } from '@emotion/react'
import styled from '@emotion/styled'

import theme from 'styles/theme'

export const BaseButtonStyles = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '54px',
  fontSize: theme.token.var('fontSizes.sm'),
  transition: 'background 0.1s ease-in, color 0.1s ease-in',
  borderRadius: theme.token.var('borderRadius.small'),
  textAlign: 'center',
  fontWeight: 600,
  cursor: 'pointer',
  // padding: '5px',

  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
})

export const NoHightBaseButtonStyles = css`
  appearance: none;
  user-select: none;
  position: relative;
  white-space: nowrap;
  vertical-align: middle;
  line-height: 1.2;
  outline: none;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  font-size: ${theme.token.var('fontSizes.sm')};
  transition: background 0.1s ease-in, color 0.1s ease-in;
  border-radius: ${theme.token.var('borderRadius.small')};
  text-align: center;
  font-weight: 600;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

// Primary button

const getPrimaryButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.green.a125') : theme.token.var('colors.green.a200')};
  color: ${theme.token.var('colors.white')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.green.a250')};
  }
`

export const StyledButton = styled.button<{ isDisabled: boolean }>(({ isDisabled }) => [
  BaseButtonStyles,
  getPrimaryButtonStyles(isDisabled),
])

export const StyledLinkButton = styled.div<{ isDisabled: boolean; height?: string }>(({ isDisabled, height }) => [
  BaseButtonStyles,
  getPrimaryButtonStyles(isDisabled),
  {
    height: height || '',
  },
])

// Secondary button

const getSecondaryButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.green.a100') : theme.token.var('colors.green.a175')};
  color: ${isDisabled ? theme.token.var('colors.green.a175') : theme.token.var('colors.green.a250')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.green.a150')};
  }
`

export const StyledSecondaryButton = styled.button<{ isDisabled: boolean }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getSecondaryButtonStyles(isDisabled)}
`

export const StyledSecondaryLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getSecondaryButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// White button

const getWhiteButtonStyles = (isDisabled: boolean) => css`
  color: ${theme.token.var('colors.green.a200')};
  background: ${theme.token.var('colors.white')};
  pointer-events: ${isDisabled ? 'none' : 'all'};
  opacity: ${isDisabled ? 0.5 : 1};

  &:hover {
    background: ${theme.token.var('colors.gray.a100')};
  }
`

export const StyledWhiteButton = styled.button<{ isDisabled: boolean }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getWhiteButtonStyles(isDisabled)}
`

export const StyledWhiteLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getWhiteButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// White outlined button

const getWhiteOutlinedButtonStyles = (isDisabled: boolean) => css`
  color: ${theme.token.var('colors.green.a200')};
  background: ${theme.token.var('colors.white')};
  pointer-events: ${isDisabled ? 'none' : 'all'};
  opacity: ${isDisabled ? 0.5 : 1};
  border: 1px solid;
  border-color: ${theme.token.var('colors.green.a200')};
  border-radius: ${theme.token.var('borderRadius.small')};

  &:hover {
    background: ${theme.token.var('colors.gray.a100')};
  }
`

export const StyledWhiteOutlinedButton = styled.button<{ isDisabled: boolean; height?: string }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getWhiteOutlinedButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

export const StyledWhiteOutlinedLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseButtonStyles};
  ${({ isDisabled }) => getWhiteOutlinedButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// Text button

export const StyledTextButton = styled.button<{ isLight: boolean; isSmall: boolean; isDimmed: boolean }>`
  font-size: ${({ isSmall }) => (isSmall ? theme.token.var('fontSizes.xs') : theme.token.var('fontSizes.sm'))};
  font-weight: ${({ isSmall }) => (isSmall ? 300 : 600)};
  color: ${({ isLight }) => (isLight ? theme.token.var('colors.white') : theme.token.var('colors.green.a200'))};
  cursor: pointer;
  opacity: ${({ isDimmed }) => (isDimmed ? 0.5 : 1)};
`

export const StyledTextLink = styled.a<{ isLight: boolean; isSmall: boolean; isDimmed: boolean }>`
  font-size: ${({ isSmall }) => (isSmall ? theme.token.var('fontSizes.xs') : theme.token.var('fontSizes.sm'))};
  color: ${({ isLight }) => (isLight ? theme.token.var('colors.white') : theme.token.var('green.a200'))};
  cursor: pointer;
  opacity: ${({ isDimmed }) => (isDimmed ? 0.5 : 1)};
`

// Danger buttons

export const StyledButtonDanger = styled.button`
  ${BaseButtonStyles};
  background: ${theme.token.var('colors.red.a75')};
  color: ${theme.token.var('colors.red.a150')};

  &:hover {
    background: ${theme.token.var('colors.red.a125')};
  }
`

export const StyledDangerTextButton = styled.button`
  font-size: ${theme.token.var('fontSizes.sm')};
  font-weight: 600;
  color: ${theme.token.var('colors.red.a150')};
  cursor: pointer;
`

// Outlined button

const getOutlinedButtonBackgroundColor = (isSelected: boolean, isDimmed: boolean, isOff: boolean) => {
  if (isSelected) {
    if (isDimmed) {
      return theme.token.var('colors.green.a150')
    }

    if (isOff) {
      return theme.token.var('colors.gray.a500')
    }

    return theme.token.var('colors.green.a200')
  }

  return theme.token.var('colors.white')
}

const getOutlinedButtonTextColor = (isSelected: boolean, isDimmed: boolean, isOff: boolean) => {
  if (!isSelected) {
    if (isDimmed) {
      return theme.token.var('colors.green.a150')
    }

    if (isOff) {
      return theme.token.var('colors.gray.a300')
    }

    return theme.token.var('colors.green.a200')
  }

  return theme.token.var('colors.white')
}

const getOutlinedButtonBorderColor = (isDimmed: boolean, isOff: boolean) => {
  if (isDimmed) {
    return theme.token.var('colors.green.a150')
  }

  if (isOff) {
    return theme.token.var('colors.gray.a300')
  }

  return theme.token.var('colors.green.a200')
}

const getOutlinedButtonHoverBackground = (isDimmed: boolean, isOff: boolean) => {
  if (isDimmed) {
    return theme.token.var('colors.green.a150')
  }

  if (isOff) {
    return theme.token.var('colors.gray.a300')
  }

  return theme.token.var('colors.green.a200')
}

const getOutlinedButtonStyles = (
  isDisabled: boolean,
  noBorder: boolean,
  isSelected: boolean,
  isDimmed: boolean,
  isOff: boolean,
) => css`
  color: ${getOutlinedButtonTextColor(isSelected, isDimmed, isOff)};
  background: ${getOutlinedButtonBackgroundColor(isSelected, isDimmed, isOff)};
  pointer-events: ${isDisabled ? 'none' : 'all'};
  opacity: ${isDisabled || isOff ? 0.5 : 1};
  border: ${noBorder ? '0px solid' : '1px solid'};
  border-color: ${getOutlinedButtonBorderColor(isDimmed, isOff)};
  border-radius: ${theme.token.var('borderRadius.big')};
  padding: 16px 16px;

  &:hover {
    background: ${getOutlinedButtonHoverBackground(isDimmed, isOff)};
    color: ${theme.token.var('colors.white')};
  }
`

export const StyledOutlinedButton = styled.button<{
  isDisabled: boolean
  height?: string
  noBorder: boolean
  isSelected: boolean
  isDimmed: boolean
  isOff: boolean
}>`
  ${NoHightBaseButtonStyles};
  ${({ isDisabled, noBorder, isSelected, isDimmed, isOff }) =>
    getOutlinedButtonStyles(isDisabled, noBorder, isSelected, isDimmed, isOff)};
`

export const StyledOutlinedLinkButton = styled.a<{
  isDisabled: boolean
  height?: string
  noBorder: boolean
  isSelected: boolean
  isDimmed: boolean
  isOff: boolean
}>`
  ${NoHightBaseButtonStyles};
  ${({ isDisabled, noBorder, isSelected, isDimmed, isOff }) =>
    getOutlinedButtonStyles(isDisabled, noBorder, isSelected, isDimmed, isOff)}
  height: ${({ height }) => height || ''};
`

// LEID buttons
export const BaseLeidButtonStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  padding: 10px;
  width: 100%;
  border-radius: ${theme.token.var('borderRadius.big')};
  font-size: ${theme.token.var('fontSizes.md')};
  transition: background 0.1s ease-in, color 0.1s ease-in;
  text-align: center;
  font-weight: 300;
  cursor: pointer;
  line-height: 21px;
  height: auto;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`

// Primary button
const getPrimaryLeidButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.gray.a100') : theme.token.var('colors.leid.lysGrnn')};
  color: ${theme.token.var('colors.black')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.green.a100')};
  }
`
export const StyledLeidButton = styled.button<{ isDisabled: boolean }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getPrimaryLeidButtonStyles(isDisabled)}
`

export const StyledLeidLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getPrimaryLeidButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// Light primary button
const getLightLeidButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.leid.lysGrnn') : theme.token.var('colors.leid.lighterGrnn')};
  color: ${theme.token.var('colors.black')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.green.a100')};
  }
`
export const StyledLeidLightButton = styled.button<{ isDisabled: boolean }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getLightLeidButtonStyles(isDisabled)}
`

export const StyledLeidLightLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getLightLeidButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// White outlined button

const getLeidWhiteOutlinedButtonStyles = (isDisabled: boolean) => css`
  color: ${theme.token.var('colors.black')};
  background: ${theme.token.var('colors.white')};
  pointer-events: ${isDisabled ? 'none' : 'all'};
  opacity: ${isDisabled ? 0.5 : 1};
  border: 1px solid;
  border-color: ${theme.token.var('colors.leid.mrkereGrnn')};

  &:hover {
    background: ${theme.token.var('colors.gray.a100')};
  }
`

export const StyledLeidWhiteOutlinedButton = styled.button<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getLeidWhiteOutlinedButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

export const StyledLeidWhiteOutlinedLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getLeidWhiteOutlinedButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// Yellow button
const getYellowLeidButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.leid.disabled') : theme.token.var('colors.leid.gul')};
  color: ${theme.token.var('colors.black')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.leid.lysGul')};
  }
`
export const StyledLeidYellowButton = styled.button<{ isDisabled: boolean }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getYellowLeidButtonStyles(isDisabled)}
`

export const StyledLeidYellowLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getYellowLeidButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`

// Red button
const getRedLeidButtonStyles = (isDisabled: boolean) => css`
  background: ${isDisabled ? theme.token.var('colors.leid.disabled') : theme.token.var('colors.updatedLeid.danger')};
  color: ${theme.token.var('colors.white')};
  pointer-events: ${isDisabled ? 'none' : 'all'};

  &:hover {
    background: ${theme.token.var('colors.updatedLeid.lightBrick')};
  }
`
export const StyledLeidRedButton = styled.button<{ isDisabled: boolean }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getRedLeidButtonStyles(isDisabled)}
`

export const StyledLeidRedLinkButton = styled.a<{ isDisabled: boolean; height?: string }>`
  ${BaseLeidButtonStyles};
  ${({ isDisabled }) => getRedLeidButtonStyles(isDisabled)}
  height: ${({ height }) => height || ''};
`
